import { AccountType } from '../models/AccountType';
import { AccountTypeValue, SchoolSettings, SchoolUser } from '../types';

export type ProfileViewAccess = 'admin_only' | 'assigned_teachers_and_admin' | 'all_staff';

export function getProfileViewAccess(settings?: SchoolSettings | null): ProfileViewAccess {
    const value = settings?.profile_view_access ?? 'admin_only';
    if (value === 'assigned_teachers_and_admin' || value === 'all_staff') {
        return value;
    }
    return 'admin_only';
}

export function canViewUserProfile(
    viewer: SchoolUser | null | undefined,
    targetId: string,
    targetType: AccountTypeValue,
    settings?: SchoolSettings | null,
    options?: {
        linkedStudentIds?: string[];
        sharedClassroomTeacherIds?: string[];
    },
): boolean {
    if (!viewer) return false;
    if (viewer.id === targetId) return true;
    if (viewer.is_superuser) return true;
    if (viewer.type === AccountType.ADMIN) return true;
    if (viewer.type === AccountType.STUDENT) return false;
    if (viewer.type === AccountType.GUARDIAN) {
        if (targetType !== AccountType.STUDENT) return false;
        return (options?.linkedStudentIds ?? []).includes(targetId);
    }
    if (viewer.type === AccountType.TEACHER) {
        const access = getProfileViewAccess(settings);
        if (access === 'admin_only') return false;
        if (access === 'all_staff') {
            return [AccountType.STUDENT, AccountType.TEACHER, AccountType.GUARDIAN].includes(targetType);
        }
        if (targetType === AccountType.STUDENT || targetType === AccountType.GUARDIAN) return true;
        if (targetType === AccountType.TEACHER) {
            return (options?.sharedClassroomTeacherIds ?? []).includes(targetId);
        }
    }
    return false;
}

export function formatProfileDisplayName(
    user: { first_name: string; last_name: string; other_names?: string | null },
    userType: string,
): string {
    if (userType === AccountType.STUDENT && user.other_names) {
        return `${user.other_names} ${user.last_name}`.trim();
    }
    return `${user.first_name} ${user.last_name}`.trim();
}

export function formatDateShort(value?: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString();
}

export const UI_EMPTY = '—';

export function formatCsvCell(value: string | number | null | undefined): string {
    if (value == null || value === '') return '-';
    const text = String(value).trim();
    if (text === '' || text === UI_EMPTY) return '-';
    return String(value);
}

export function exportRowsToCsv(
    filename: string,
    headers: string[],
    rows: Array<Array<string | number | null | undefined>>,
): void {
    const escape = (cell: string) => `"${cell.replace(/"/g, '""')}"`;
    const lines = [
        headers.map((header) => escape(formatCsvCell(header))).join(','),
        ...rows.map((row) => row.map((cell) => escape(formatCsvCell(cell))).join(',')),
    ];
    const blob = new Blob(['\uFEFF', lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

export function ordinalSuffix(n: number): string {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
}

export function formatDeltaVsClassAverage(delta: number): string {
    const rounded = Math.round(Math.abs(delta) * 100) / 100;
    const value = parseFloat(rounded.toFixed(2)).toString();
    if (delta >= 0) {
        return `+${value} above average`;
    }
    return `${value} below average`;
}
